/**
 * v130 — ActiveSession: mobile-first shot-by-shot capture UI.
 *
 * Design constraint: 3-5 second per-shot capture loop while standing at the
 * line. The UI is large-touch-target, low-chrome, single-column. Distance +
 * pin "stick" between shots so a series at the same yardage is just
 * "tap target → Log shot → tap target → Log shot."
 *
 * Layout (mobile):
 *   Header        — Session name + shot count + End session
 *   Setup chip    — Bow + arrow snapshot (collapsible)
 *   Distance bar  — Preset pills (20/30/40/50/60/70 + custom)
 *   ShotPlot      — Tap-to-place target SVG
 *   Pin/Wind/Note — Compact row (only Pin is required)
 *   Log shot CTA  — Sticky full-width ember button
 *   Recent shots  — Mini-list under the CTA (last 5, swipe to delete)
 *
 * On commit (End session), the parent should:
 *   1. Call useRange.logSession() with aggregate fields + v2 extensions
 *   2. Call useActiveSession.end() to clear the active record
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { X, Plus, Trash2, Check, Wind, Pin as PinIcon, FileText, Crosshair, Sliders, Image as ImageIcon } from 'lucide-react';
import clsx from 'clsx';
import { ShotPlot } from './ShotPlot';
import { AdvancedPlotControls } from './AdvancedPlotControls';
import { SetManager } from './SetManager';
import { PhotoTargetOverlay } from './PhotoTargetOverlay';
import { DistancePicker } from './DistancePicker';
import { computeShotAggregates } from '../aggregates';
import type { ActiveSession as ActiveSessionT } from '../useActiveSession';
import type { RichShot } from '../v2-schema';
import type { CaptureMode, DisplayUnit, ShotSet, PhotoCalibration } from '../v3-schema';

interface ActiveSessionProps {
  session: ActiveSessionT;
  onAddShot: (shot: Omit<RichShot, 'id' | 'shotAt'>) => void;
  onRemoveShot: (id: string) => void;
  /** End session and commit it to the permanent log. */
  onEnd: () => void;
  /** Abandon session without committing — confirmation handled by parent. */
  onAbandon: () => void;
  /** v132 — capture mode + unit prefs + set lifecycle. */
  captureMode: CaptureMode;
  onCaptureModeChange: (m: CaptureMode) => void;
  unit: DisplayUnit;
  onUnitChange: (u: DisplayUnit) => void;
  onStartNewSet: (opts: { label?: string; setupDelta?: string; notes?: string }) => void;
  onPatchCurrentSet: (p: Partial<Pick<ShotSet, 'label' | 'setupDelta' | 'notes'>>) => void;
  /** v133 — write the photo + calibration onto the open set. */
  onSetPhoto?: (p: { photoDataUri: string; calibration: PhotoCalibration }) => void;
}

// v133.2 — distance picker logic moved into shared DistancePicker.tsx so
// Live Session capture, Live Session start modal, and Quick Log all use the
// same control. The shared component owns the chip presets, custom-distance
// input, recent-distances row, and 150/200-yd thresholds.

export function ActiveSession({
  session, onAddShot, onRemoveShot, onEnd, onAbandon,
  captureMode, onCaptureModeChange, unit, onUnitChange,
  onStartNewSet, onPatchCurrentSet, onSetPhoto,
}: ActiveSessionProps) {
  const [distance, setDistance] = useState<number>(session.lastDistanceYd ?? 40);
  const [pin, setPin] = useState<string>(session.lastPin ?? '40');
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null);
  // v133 — photo overlay open state
  const [photoOpen, setPhotoOpen] = useState(false);
  const currentSet = session.sets && session.sets.length > 0 ? session.sets[session.sets.length - 1] : undefined;
  const advancedScrollRef = useRef<HTMLDivElement | null>(null);

  // v133.1 — when the user switches to Advanced mode, scroll the slider controls
  // into view so they're not below the fold on phone viewports.
  useEffect(() => {
    if (captureMode === 'advanced' && advancedScrollRef.current) {
      // setTimeout to wait for the render cycle to mount the controls.
      const id = window.setTimeout(() => {
        advancedScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return () => window.clearTimeout(id);
    }
  }, [captureMode]);

  const commitDistance = (n: number) => {
    // Shared DistancePicker already clamped + warned. Just sync the pin.
    setDistance(n);
    setPin(String(n));
  };
  const [wind, setWind] = useState<string>(session.conditions?.windMph?.toString() ?? '');
  const [note, setNote] = useState<string>('');
  const [showNote, setShowNote] = useState<boolean>(false);
  const [targetType, setTargetType] = useState<RichShot['targetType']>('3d');

  const aggs = computeShotAggregates(session.shots);

  const logShot = () => {
    if (!pending) return;
    onAddShot({
      distanceYd: distance,
      targetType,
      aimPoint: 'center',
      hitXIn: pending.x,
      hitYIn: pending.y,
      pin,
      windMph: wind ? parseFloat(wind) : undefined,
      notes: note || undefined,
    });
    setPending(null);
    setNote('');
    setShowNote(false);
  };

  const recentShots = session.shots.slice(-5).reverse();
  const startedAtFmt = new Date(session.startedAt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <div className="space-y-3 max-w-2xl mx-auto pb-32 sm:pb-6">
      {/* ───── HEADER ───── */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-cyan-400">
            Live session · {startedAtFmt}
          </p>
          <h2 className="text-lg font-bold text-zinc-100 truncate">
            {session.location || 'Range session'}
          </h2>
        </div>
        <button
          onClick={onAbandon}
          className="text-zinc-500 hover:text-rose-400 p-2 -m-2 touch-manipulation"
          aria-label="Abandon session"
          title="Abandon — discards unsaved shots"
        >
          <X size={20} />
        </button>
      </div>

      {/* ───── SHOT COUNT + AGGREGATES STRIP ───── */}
      <div className="grid grid-cols-4 gap-2">
        <MicroStat label="Shots" value={`${session.shots.length}`} />
        <MicroStat label="Mean r" value={aggs ? `${aggs.meanRadiusIn.toFixed(2)}″` : '—'} />
        <MicroStat label="95% r" value={aggs ? `${aggs.group95RadiusIn.toFixed(2)}″` : '—'} />
        <MicroStat label="Best" value={aggs ? `${aggs.bestRadiusIn.toFixed(2)}″` : '—'} />
      </div>

      {/* ───── DISTANCE PICKER (shared) ───── */}
      <DistancePicker value={distance} onChange={commitDistance} />

      {/* ───── TARGET TYPE ───── */}
      <div className="flex flex-wrap gap-1.5">
        {(['3d', 'paper', '3-spot', 'block'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTargetType(t)}
            className={clsx(
              'px-3 py-1.5 rounded text-[12px] font-mono uppercase tracking-wider border touch-manipulation',
              targetType === t
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/60'
                : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ───── CAPTURE MODE TOGGLE ───── */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex border border-zinc-800 rounded overflow-hidden">
          <button
            onClick={() => onCaptureModeChange('quick')}
            className={clsx('px-3 py-1.5 min-h-[44px] text-[12px] font-mono uppercase tracking-wider flex items-center gap-1 touch-manipulation',
              captureMode === 'quick' ? 'bg-cyan-500/20 text-cyan-300' : 'text-zinc-400 hover:bg-zinc-900')}
          >
            <Crosshair size={11} />Quick
          </button>
          <button
            onClick={() => onCaptureModeChange('advanced')}
            className={clsx('px-3 py-1.5 min-h-[44px] text-[12px] font-mono uppercase tracking-wider flex items-center gap-1 touch-manipulation',
              captureMode === 'advanced' ? 'bg-cyan-500/20 text-cyan-300' : 'text-zinc-400 hover:bg-zinc-900')}
          >
            <Sliders size={11} />Advanced
          </button>
        </div>
        {/* v133 — Photo capture entry */}
        <button
          onClick={() => setPhotoOpen(true)}
          className="px-3 py-1.5 min-h-[44px] text-[12px] font-mono uppercase tracking-wider flex items-center gap-1 border border-ember-500/40 text-ember-300 rounded hover:bg-ember-500/10 touch-manipulation"
          title="Photo-anchored logging — calibrate a photo and tap each arrow hole"
        >
          <ImageIcon size={11} />Use photo
        </button>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-auto">
          {captureMode === 'quick' ? 'Tap target to place' : 'Precise numeric entry'}
        </span>
      </div>

      {/* v133.1 — surface a one-line hint so users discover Advanced + photo modes. */}
      {captureMode === 'quick' && (
        <p className="text-[11px] text-zinc-500 text-center -mt-1">
          Need precision? Tap <span className="text-cyan-300 font-mono">Advanced</span> for X/Y sliders + numeric input · or <span className="text-ember-300 font-mono">Use photo</span> to log from a target picture.
        </p>
      )}

      {/* ───── PLOT ───── */}
      <Card padding="md" className="bg-zinc-900/30">
        <ShotPlot
          shots={session.shots}
          pending={pending}
          onPlace={captureMode === 'quick' ? (x, y) => setPending({ x, y }) : () => { /* placement via controls */ }}
          targetType={targetType}
        />
        {captureMode === 'advanced' && (
          <div className="mt-3" ref={advancedScrollRef}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-300 mb-1 flex items-center gap-1">
              <Sliders size={10} /> Advanced controls
            </div>
            <AdvancedPlotControls
              pending={pending ?? { x: 0, y: 0 }}
              onChange={(x, y) => setPending({ x, y })}
              unit={unit}
              onUnitToggle={onUnitChange}
            />
          </div>
        )}
        {captureMode === 'quick' && (
          <p className="text-[11px] text-zinc-500 text-center mt-2">
            {pending
              ? `Pending: ${pending.x.toFixed(2)}″ ${pending.x >= 0 ? 'R' : 'L'} · ${pending.y.toFixed(2)}″ ${pending.y >= 0 ? 'down' : 'up'}`
              : 'Tap the target where the arrow hit'}
          </p>
        )}
      </Card>

      {/* ───── PIN + WIND + NOTE ROW ───── */}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-1">
            <PinIcon size={10} />Pin
          </span>
          <input
            type="text"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
            placeholder={`${distance} or "slider"`}
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-1">
            <Wind size={10} />Wind (mph)
          </span>
          <input
            type="number"
            value={wind}
            onChange={(e) => setWind(e.target.value)}
            step={0.5}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
            placeholder="optional"
          />
        </label>
      </div>

      {showNote ? (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
          placeholder="One-line note for this shot"
          autoFocus
        />
      ) : (
        <button
          onClick={() => setShowNote(true)}
          className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 mx-auto py-1"
        >
          <FileText size={11} /> Add note (optional)
        </button>
      )}

      {/* ───── STICKY LOG-SHOT CTA + END SESSION ───── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 sm:static sm:z-auto px-3 py-3 sm:p-0 bg-zinc-950/95 sm:bg-transparent backdrop-blur-md sm:backdrop-blur-none border-t border-zinc-800 sm:border-0 flex gap-2">
        <Button
          variant="secondary"
          size="md"
          onClick={onEnd}
          className="flex-shrink-0"
        >
          <Check size={14} className="mr-1.5" />End session
        </Button>
        <Button
          size="lg"
          onClick={logShot}
          disabled={!pending}
          className="flex-1"
        >
          <Plus size={16} className="mr-1.5" />Log shot
        </Button>
      </div>

      {/* ───── RECENT SHOTS ───── */}
      {recentShots.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            Recent shots (last {recentShots.length})
          </p>
          <ul className="space-y-1">
            {recentShots.map((s, i) => {
              const num = session.shots.length - i;
              const r = Math.sqrt(s.hitXIn ** 2 + s.hitYIn ** 2);
              return (
                <li
                  key={s.id}
                  className="flex items-center gap-2 bg-zinc-900/40 border border-zinc-800 rounded px-2 py-1.5"
                >
                  <span className="text-[10px] font-mono text-ember-500 w-7">#{num}</span>
                  <span className="text-xs text-zinc-300 flex-1">
                    {s.distanceYd}yd · {r.toFixed(2)}″ r · {s.hitXIn >= 0 ? 'R' : 'L'}{Math.abs(s.hitXIn).toFixed(1)} {s.hitYIn >= 0 ? 'D' : 'U'}{Math.abs(s.hitYIn).toFixed(1)}
                  </span>
                  <button
                    onClick={() => onRemoveShot(s.id)}
                    className="text-zinc-600 hover:text-rose-400 p-1.5 touch-manipulation"
                    aria-label="Remove this shot"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ───── SETS ───── */}
      <SetManager
        sets={session.sets ?? []}
        shots={session.shots}
        onStartNewSet={onStartNewSet}
        onPatchCurrentSet={onPatchCurrentSet}
      />

      {/* ───── PHOTO OVERLAY ───── */}
      {photoOpen && (
        <PhotoTargetOverlay
          initialPhotoDataUri={currentSet?.photoDataUri}
          initialCalibration={currentSet?.photoCalibration}
          existingShots={session.shots.filter((s) =>
            currentSet ? currentSet.shotIds.includes(s.id) : true,
          ).map((s) => ({ id: s.id, hitXIn: s.hitXIn, hitYIn: s.hitYIn }))}
          distanceYd={distance}
          targetType={targetType}
          pin={pin}
          onCancel={() => setPhotoOpen(false)}
          onCommit={({ newShots, photoDataUri, calibration }) => {
            // Write photo + calibration onto the open set
            if (onSetPhoto) onSetPhoto({ photoDataUri, calibration });
            // Commit each placed dot as a RichShot via the existing API.
            for (const s of newShots) {
              onAddShot({
                distanceYd: distance,
                targetType,
                aimPoint: 'center',
                pin,
                hitXIn: s.hitXIn,
                hitYIn: s.hitYIn,
              });
            }
            setPhotoOpen(false);
          }}
        />
      )}
    </div>
  );
}

function MicroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded px-2 py-1.5">
      <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-sm font-bold text-zinc-100 truncate">{value}</p>
    </div>
  );
}
